// ============================================================
// foto-fondo.js — SSOT del FRONT de "aquí el fondo es una FOTO, no `bgColor`".
//
// EL HUECO QUE TAPA: cuando el hero saca la foto a fondo, `bgColor` deja de ser el fondo y
// pasa a ser solo el color del VELO (o, sin velo, el del degradado de respaldo que pinta
// `editor/unsplash.js`). Nadie en el front lo sabía, así que CUATRO capas recalculaban el
// `textColor` contrastándolo contra un color que no pinta nadie:
//   · `utils/contrast.js::safe()`            — en cada render de cada bloque;
//   · `utils/palettes.js::applyPaletteToBlocks` — al re-paletizar desde el Estudio de Tema;
//   · `editor/section-color.js::trio()`      — panel "Pintar esta sección" (que además hornea
//     `bgBase`/`textBase`, así que el valor malo sobrevivía a re-paletizados posteriores);
//   · `editor/gradient-panel.js`             — el cliente baja el velo y NADA recalculaba.
// El backend ya medía bien (`llm_sanitizers._fix_foto_fondo`), pero cualquiera de esas cuatro
// deshacía su trabajo en cuanto el cliente abría el editor.
//
// PARIDAD CON PYTHON: `FOTO_FONDO_VARIANTES`, `FOTO_GRIS` y `CONTRASTE_MINIMO_FOTO` son espejo
// TEXTUAL de `_FOTO_FONDO_VARIANTES`, `_FOTO_GRIS` y `_CONTRASTE_MINIMO_FOTO`
// (`core_services/llm_sanitizers.py`); lo guarda `tests/test_foto_fondo.py`. Y NO se funden con
// `_BG_IMAGE_VARIANTS` (que incluye `banner` v2): son dos listas distintas a propósito — la de
// allí contesta "¿quién pinta una foto de fondo?" para la regla del `ghost_text`, y la de aquí
// "¿sobre quién actúa la regla de contraste?". El banner v2 queda fuera porque ya trae su velo
// en CSS (`.banner-overlay`) y cablea `color:#ffffff`.
//
// MÓDULO PURO (sin DOM, sin store) y en **ES2019 sin `?.` ni `??`**: lo carga el bundle
// PÚBLICO a través de `contrast.js` y lo ejercita el arnés de node (v12.22.9 en este entorno,
// que rechaza esa sintaxis; no lo caza el build —esbuild transpila— sino el arnés).
// Por lo mismo solo importa de `contrast.js`: la matemática de color del editor vive en
// `utils/palettes.js`, y arrastrarla aquí la metería en el bundle de todos los visitantes.
// ============================================================

import { hexToRgb, rgbToHex, wcagRatio, checkAndFixContrast, TARGET_TEXT } from './contrast.js';

// Variantes sobre las que ACTÚA la regla. Espejo de `llm_sanitizers._FOTO_FONDO_VARIANTES`.
export var FOTO_FONDO_VARIANTES = { hero: ['v1', 'v2'] };

// La foto es desconocida (la resuelve Unsplash en runtime) → se modela como gris medio: la
// apuesta neutra, ni regala contraste ni lo castiga. Espejo de `_FOTO_GRIS`.
export var FOTO_GRIS = [128, 128, 128];

// Espejo de `_CONTRASTE_MINIMO_FOTO`: por debajo de esto, el titular no se lee.
export var CONTRASTE_MINIMO_FOTO = 4.5;

// Velo de RESPALDO que pinta `editor/unsplash.js` cuando el bloque no trae `bg_gradient`:
// `linear-gradient(${bgColor}e6, ${bgColor}99)`, o sea el propio `bgColor` al 90 % arriba y al
// 60 % abajo (0xe6 = 230/255, 0x99 = 153/255). Es front-only a propósito: el saneador de Python
// nunca ve ese caso porque al generar CREA un velo de verdad. Sin modelarlo, un hero sin velo
// se contrastaría contra `bgColor` a plena fuerza, que es un fondo que el visitante no ve.
var VELO_FALLBACK = [90, 60];

var _ES_HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

// Lo que `contrast.js::hexToRgb` SABE leer: hex de 3 o 6 dígitos **y** `rgb()/rgba()`. Es más
// ancho que `esHex` a propósito, porque es otra pregunta: `esHex` decide si un valor sirve como
// COLOR DE UN STOP del velo, y esto decide si se le puede calcular un RATIO. La guarda de "nunca
// empeorar" necesita la segunda: `views._sanitizar_props_css` deja pasar `rgb()/rgba()`,
// `color-mix()`, `var(--x)` y los nombres CSS, y con la guarda colgada de `esHex` un
// `rgb(255,255,255)` se la saltaba entera y acababa en el respaldo nuclear (medido sobre el velo
// débil `#1e3a8a@12`: 4.42 → 4.01, o sea EMPEORANDO). Espejo de `llm_sanitizers._es_medible`.
var _ES_MEDIBLE = /^(?:#(?:[0-9a-f]{3}|[0-9a-f]{6})|rgba?\([^)]*\))$/i;

/** ¿`v` es un hex utilizable? (espejo de `llm_sanitizers._is_hex`, ampliado al #abc del front). */
export function esHex(v) {
    return typeof v === 'string' && _ES_HEX.test(v.trim());
}

/** ¿Se puede calcular el ratio WCAG de `v`? (espejo de `llm_sanitizers._es_medible`). */
export function esMedible(v) {
    return typeof v === 'string' && _ES_MEDIBLE.test(v.trim());
}

function _prop(props, clave) {
    return (props && typeof props === 'object') ? props[clave] : undefined;
}

/**
 * ¿Esta sección saca la foto a FONDO y le aplica la regla de contraste?
 *
 * Es la MISMA puerta que `llm_sanitizers._fix_foto_fondo` (variante de `FOTO_FONDO_VARIANTES`
 * + sin vídeo + `use_image` + foto), no la de `_pinta_foto_de_fondo`. El vídeo queda fuera
 * porque `blocks/hero.js` le pinta su propio degradado (`renderVideoBackground`): medir contra
 * un `bg_gradient` que ahí no manda daría un color equivocado. Y `use_image` es la puerta
 * canónica del repo (la escribe `editor/catalog.js`, la lee `render.js::hasImage`): sin ella,
 * un hero con una `image_keyword` suelta se llevaría un texto pensado para una foto que NADIE
 * pinta, sobre su `bgColor` blanco = titular invisible.
 */
export function pintaFotoDeFondo(props, type) {
    if (!props || typeof props !== 'object') return false;
    var variantes = FOTO_FONDO_VARIANTES[type];
    if (!variantes) return false;
    var variant = props.variant || 'v1';
    if (variantes.indexOf(variant) === -1) return false;
    if (props.video_url) return false;
    if (!props.use_image) return false;
    return !!(props.image_url || props.image_keyword);
}

/**
 * Color EFECTIVO de UN stop del velo compuesto sobre la foto. `null` si el stop no sirve.
 * Espejo de `llm_sanitizers._color_sobre_foto` — incluido el guard de tipo POR DELANTE: el
 * `bg_gradient` lo escribe el LLM y puede traer un string suelto entre los stops.
 */
export function colorSobreFoto(stop) {
    if (!stop || typeof stop !== 'object') return null;
    if (!esHex(stop.color)) return null;
    var op = parseInt(stop.opacity, 10);
    if (isNaN(op)) op = 0;
    op = Math.max(0, Math.min(100, op)) / 100;
    var rgb = hexToRgb(stop.color);
    return rgbToHex(
        Math.round(rgb[0] * op + FOTO_GRIS[0] * (1 - op)),
        Math.round(rgb[1] * op + FOTO_GRIS[1] * (1 - op)),
        Math.round(rgb[2] * op + FOTO_GRIS[2] * (1 - op))
    );
}

/** Los stops que de verdad se pintan: el `bg_gradient` del bloque o el velo de respaldo. */
function _stopsDelVelo(bgGradient, bgColor) {
    if (Array.isArray(bgGradient) && bgGradient.length) return bgGradient;
    if (esHex(bgColor)) {
        return VELO_FALLBACK.map(function (op) { return { color: bgColor, opacity: op }; });
    }
    return [];
}

/**
 * Peor ratio de `texto` sobre el velo ENTERO = MIN sobre todos los stops utilizables. Espejo de
 * `llm_sanitizers._peor_ratio`. `null` si no hay ni un stop que medir.
 *
 * No es lo mismo que medir contra `colorEfectivoSobreFoto(...)`: ese devuelve la peor zona DEL
 * TEXTO ACTUAL, y en cuanto el texto cambia la peor zona puede ser OTRA. Comparar dos colores
 * en un solo fondo dice quién gana ahí, no quién gana en la sección.
 */
function _peorRatioDelVelo(stops, texto) {
    var peor = null;
    for (var i = 0; i < stops.length; i++) {
        var eff = colorSobreFoto(stops[i]);
        if (!eff) continue;
        var r = wcagRatio(eff, texto);
        if (peor === null || r < peor) peor = r;
    }
    return peor;
}

/**
 * El fondo contra el que hay que medir el texto: el color efectivo del velo sobre la foto en
 * su PEOR zona (el titular cruza la sección entera). Con `textoActual` la peor zona es la de
 * menor contraste CON ESE TEXTO —la misma definición que usa el saneador de Python—; sin él,
 * la de luminancia más cercana al gris de la foto, que es la más difícil para cualquier texto.
 * Devuelve `null` si no hay ni un stop utilizable (nadie pinta velo: no hay nada que medir).
 */
export function colorEfectivoSobreFoto(bgGradient, bgColor, textoActual) {
    var stops = _stopsDelVelo(bgGradient, bgColor);
    var peor = null;
    var peorPuntos = null;
    var grisHex = rgbToHex(FOTO_GRIS[0], FOTO_GRIS[1], FOTO_GRIS[2]);
    for (var i = 0; i < stops.length; i++) {
        var eff = colorSobreFoto(stops[i]);
        if (!eff) continue;
        // Con texto: gana el de MENOR ratio. Sin texto: el más parecido al gris de la foto
        // (ratio contra el gris más bajo = más indistinguible) — misma comparación, un solo
        // bucle, sin ramas duplicadas.
        var puntos = wcagRatio(eff, esHex(textoActual) ? textoActual.trim() : grisHex);
        if (peorPuntos === null || puntos < peorPuntos) {
            peor = eff;
            peorPuntos = puntos;
        }
    }
    return peor;
}

/**
 * El `textColor` que hay que usar para este bloque con foto de fondo.
 *
 * Reusa `checkAndFixContrast` (el algoritmo de contraste del repo, port 1:1 de
 * `color_utils.fix_contrast`) pero contra el fondo REAL, y con una guarda de **NUNCA
 * EMPEORAR**: ese algoritmo no es monótono —si el objetivo es inalcanzable agota sus 15
 * iteraciones y devuelve el respaldo nuclear— y sobre una foto con velo débil el respaldo
 * mide MENOS que el texto que ya había. Medido: velo `#1e3a8a@12` → efectivo `#747881`;
 * blanco 4.42, respaldo `#111827` 3.93. Sin esta guarda, abrir el editor de un sitio ya
 * generado ROMPÍA lo que el backend había dejado bien.
 *
 * NO ahonda el velo ni inventa uno: eso es decisión de generación (Python). Aquí solo se elige
 * el color del texto, que es lo que las cuatro capas del front ya hacían (mal).
 */
export function textoSobreFoto(props, type) {
    var actual = _prop(props, 'textColor');
    if (!pintaFotoDeFondo(props, type)) return actual;
    var stops = _stopsDelVelo(_prop(props, 'bg_gradient'), _prop(props, 'bgColor'));
    var fondo = colorEfectivoSobreFoto(_prop(props, 'bg_gradient'), _prop(props, 'bgColor'), actual);
    if (!fondo) return actual;
    // La guarda se aplica a TODO lo que se puede medir (`esMedible`: hex de 3/6 y `rgb()/rgba()`),
    // no solo a lo que es hex. Colgada de `esHex`, un `rgb(255,255,255)` —formato que el saneador
    // de props ADMITE— se saltaba la guarda y se llevaba el respaldo nuclear aunque midiera peor.
    // Lo que de verdad no se puede medir (`white`, `color-mix()`, `var(--x)`: `hexToRgb` los lee
    // como negro) se queda como estaba antes de este PDR: corregir a ciegas es lo único que se
    // puede hacer ahí, y fingir una guarda con un ratio inventado sería peor.
    if (!esMedible(actual)) return checkAndFixContrast(fondo, actual, TARGET_TEXT);
    var propuesto = checkAndFixContrast(fondo, actual, TARGET_TEXT);
    // La guarda se mide sobre TODO el velo (`_peorRatioDelVelo`), no solo sobre la peor zona del
    // texto ACTUAL: al cambiar el color cambia cuál es la peor zona, así que un candidato puede
    // ganar en la zona donde se le eligió y perder en otra. Medido sobre la BD local (hero del
    // sitio 24, velo `[{#38200a,25},{#38280b,0}]`): `#f1f5f9` mide 3.61 en su peor zona; el
    // respaldo nuclear `#111827` mide 4.40 en ESA zona pero 3.23 en la otra → con la guarda de
    // un solo fondo el titular EMPEORABA, justo lo que esta función promete que no pasa. Es la
    // misma definición que `llm_sanitizers._peor_ratio`, que es lo que hace que las dos mitades
    // de esta SSOT decidan igual.
    var rActual = _peorRatioDelVelo(stops, actual.trim());
    var rPropuesto = _peorRatioDelVelo(stops, propuesto);
    if (rActual === null || rPropuesto === null) return actual;
    return rPropuesto > rActual ? propuesto : actual;
}

// ── EL DEGRADADO DEL TITULAR (§3.3, opción A del dueño, 28-09-2026) ──────────────────────────
// `.title-gradient` (css/site.css) pinta el titular del hero de `--text` a
// `color-mix(in srgb, var(--secondary, var(--accent)) var(--title-mix, 62%), var(--text))`. El
// TEXTO se mide contra lo que tiene detrás (`safe()` → `textoSobreFoto`), pero el secundario y el
// acento solo contra `bgColor` (`secondaryOf`, `safe()`), y con la foto de fondo `bgColor` solo
// colorea el velo: el FINAL del degradado no lo medía nadie. Museo del ferrocarril: texto `#f2ddd9`
// + secundario `#392c14` → final `#7f6f5f`, la última palabra a 2,86:1 medida en píxeles.
// La regla: si con el peso de siempre el final no llega a 3:1 (AA de texto grande; el titular del
// hero lo es siempre) contra el MISMO fondo que el texto, se rebaja el peso de la marca lo justo —el
// mayor que llega— y el render lo emite como `--title-mix` en el <h1>. El que ya llega no emite nada.
export var PESO_MARCA_TITULAR = 62;          // el 62 % de siempre: espejo del fallback de site.css
export var CONTRASTE_MINIMO_DEGRADADO = 3;

/**
 * `color-mix(in srgb, a peso%, b)`: la mezcla que hace el navegador, en sRGB codificado, canal a
 * canal y redondeada a hex. `null` si alguno de los dos no se puede leer.
 */
export function mezclaSrgb(a, b, peso) {
    if (!esMedible(a) || !esMedible(b)) return null;
    var A = hexToRgb(a.trim());
    var B = hexToRgb(b.trim());
    var w = peso / 100;
    return rgbToHex(A[0] * w + B[0] * (1 - w), A[1] * w + B[1] * (1 - w), A[2] * w + B[2] * (1 - w));
}

/**
 * Lo que el titular del hero tiene DETRÁS, una zona por stop: la foto con su velo si la foto es el
 * fondo (`pintaFotoDeFondo`: v1/v2), la foto desnuda si no hay velo que pintar, y si no `bgColor`.
 * Es el mismo fondo contra el que `safe()` mide el texto (en el v4 el titular va sobre su panel, que
 * es `bgColor` al 90 %). `props` = las que ya pasaron por `safe()`. [] si no hay nada que medir.
 */
export function fondosDelTitular(props, type) {
    if (pintaFotoDeFondo(props, type)) {
        var stops = _stopsDelVelo(_prop(props, 'bg_gradient'), _prop(props, 'bgColor'));
        var zonas = [];
        for (var i = 0; i < stops.length; i++) {
            var eff = colorSobreFoto(stops[i]);
            if (eff) zonas.push(eff);
        }
        return zonas.length ? zonas : [rgbToHex(FOTO_GRIS[0], FOTO_GRIS[1], FOTO_GRIS[2])];
    }
    var bg = _prop(props, 'bgColor');
    return esMedible(bg) ? [bg.trim()] : [];
}

/**
 * El peso de la marca (0-62) con el que el FINAL del degradado llega a 3:1 en la PEOR zona de
 * `fondos`, o `null` si el de siempre ya llega (o no hay nada que medir: entonces no se toca). Se
 * busca de 62 hacia abajo y gana el PRIMERO que llega: se rebaja solo lo necesario y el final queda
 * siempre entre la marca y el texto (con 0, el titular es del color del texto). Si ninguno llega,
 * el que MÁS se acerca: nunca peor que hoy, porque el 62 también compite.
 */
export function pesoMarcaDelTitular(texto, marca, fondos) {
    if (!esMedible(texto) || !esMedible(marca) || !fondos || !fondos.length) return null;
    var peor = function (color) {
        var m = null;
        for (var i = 0; i < fondos.length; i++) {
            var r = wcagRatio(color, fondos[i]);
            if (m === null || r < m) m = r;
        }
        return m;
    };
    var mejor = PESO_MARCA_TITULAR;
    var mejorRatio = -1;
    for (var k = PESO_MARCA_TITULAR; k >= 0; k--) {
        var r = peor(mezclaSrgb(marca, texto, k));
        if (r >= CONTRASTE_MINIMO_DEGRADADO) return k === PESO_MARCA_TITULAR ? null : k;
        if (r > mejorRatio) {
            mejor = k;
            mejorRatio = r;
        }
    }
    return mejor === PESO_MARCA_TITULAR ? null : mejor;
}
